import { truncate } from "lodash-es";
import moment from "moment-timezone";
import { useState } from "react";
import LinkButton from "src/components/buttons/LinkButton";
import MembershipOfferingModel from "src/models/MembershipOfferingModel";
import { dateFormat } from "src/utils/constants";
import formatMoney from "src/utils/formatMoney";
import { Frequency } from "types/code-generator";

interface Props {
  offering: MembershipOfferingModel;
  isDetail?: boolean;
  onExpand?: () => void;
}

export function MembershipOfferingDetail(props: Props) {
  const [viewingProgramDescription, setViewingProgramDescription] =
    useState(false);
  const [viewingOfferingDescription, setViewingOfferingDescription] =
    useState(false);

  function viewProgramDescription() {
    setViewingProgramDescription((current) => !current);

    if (props.onExpand) {
      props.onExpand();
    }
  }

  function viewOfferingDescription() {
    setViewingOfferingDescription((current) => !current);

    if (props.onExpand) {
      props.onExpand();
    }
  }

  function renderPricingDetails() {
    const frequency =
      props.offering.paymentFrequency === Frequency.Monthly
        ? "monthly"
        : props.offering.paymentFrequency === Frequency.Annual
          ? "annually"
          : "one-time";

    let price = (
      <div>
        <strong>
          ${formatMoney(props.offering.price || 0)} {frequency}
        </strong>
      </div>
    );

    if (
      (!props.offering.price || props.offering.price === 0) &&
      props.offering.freePresentation !== null
    ) {
      price = (
        <div>
          <strong>{props.offering.freePresentation}</strong>
        </div>
      );
    }

    if (props.offering.priceLevels.length > 0) {
      price = (
        <>
          {props.offering.priceLevels
            .sort((a, b) => a.number - b.number)
            .map((p) => {
              let label = "people are members";

              if (p.number === 1) {
                label = "person is a member";
              }

              return (
                <div key={p.id} className="mb-2">
                  When {p.number} {label} on your account
                  <div className="font-bold">
                    ${formatMoney(p.price)} {frequency}
                  </div>
                </div>
              );
            })}
        </>
      );
    }

    return (
      <div className="mb-4">
        <h3 className="step-subhead">Pricing Details</h3>
        {/* <div>Single charge per account</div> */}
        {price}
      </div>
    );
  }

  function renderExpirationDetails() {
    if (!props.offering.endDate && !props.offering.endDays) {
      return null;
    }

    let expiration;

    if (props.offering.endDate && !props.offering.endDays) {
      expiration = `On ${moment(props.offering.endDate).format(dateFormat)}`;
    } else if (props.offering.endDays && !props.offering.endDate) {
      expiration = `${props.offering.endDays} days after registration`;
    } else if (props.offering.endDate && props.offering.endDays) {
      expiration = `On ${moment(props.offering.endDate).format(
        dateFormat
      )} or ${
        props.offering.endDays
      } days after registration, whichever is earlier`;
    }

    return (
      <div className="mb-4">
        <h3 className="step-subhead">This Membership Automatically Expires</h3>
        {expiration}
      </div>
    );
  }

  let offeringDescription;
  let programDescription;

  if (
    props.offering.description &&
    props.offering.description.length > 100 &&
    !viewingOfferingDescription &&
    !props.isDetail
  ) {
    offeringDescription = (
      <p>
        {truncate(props.offering.description, { length: 100, omission: "…" })}{" "}
        <LinkButton onClick={viewOfferingDescription}>expand</LinkButton>
      </p>
    );
  } else if (props.offering.description) {
    offeringDescription = (
      <div>
        <div dangerouslySetInnerHTML={{ __html: props.offering.description }} />
        {props.offering.description.length > 100 && !props.isDetail ? (
          <LinkButton onClick={viewOfferingDescription}>collapse</LinkButton>
        ) : null}
      </div>
    );
  }

  const pricingDetails = renderPricingDetails();
  const expirationDetails = renderExpirationDetails();

  if (
    props.offering.program?.description?.length > 100 &&
    !viewingProgramDescription &&
    !props.isDetail
  ) {
    programDescription = (
      <p>
        {truncate(props.offering.program?.description, {
          length: 100,
          omission: "…",
        })}{" "}
        <LinkButton onClick={viewProgramDescription}>expand</LinkButton>
      </p>
    );
  } else if (props.offering.program?.description) {
    programDescription = (
      <p>
        {props.offering.program.description.split("\n").map((item, key) => {
          return (
            <span key={key}>
              {item}
              <br />
            </span>
          );
        })}
        {props.offering.program.description.length > 100 && !props.isDetail ? (
          <LinkButton onClick={viewProgramDescription}>collapse</LinkButton>
        ) : null}
      </p>
    );
  }

  return (
    <>
      {props.isDetail && <hr className="mt-8" />}
      <div className="pt-4">
        {programDescription && (
          <>
            <h3 className="step-subhead">Program Description</h3>
            <div
              className="mb-4"
              dangerouslySetInnerHTML={{
                __html: props.offering.program?.description?.replace(
                  /<p><br><\/p>/g,
                  ""
                ),
              }}
            />
          </>
        )}

        {offeringDescription && (
          <>
            <h3 className="step-subhead">{props.offering.type} Description</h3>
            <div
              className="mb-4"
              dangerouslySetInnerHTML={{
                __html: props.offering.description,
              }}
            />
          </>
        )}
        {pricingDetails}
        {expirationDetails}
      </div>
    </>
  );
}
